import { TechCalcCardDocument } from "./components/TechCalcCardDocument";
import * as api from "./api";
import type { PoleResult, TechCalcDrawing, TechCalcPointFeature, VariantResults } from "./types";
import { compareRefs, drawingPoints, poleDisplayLabel } from "./utils";

/**
 * Eksport kart obliczeń do PDF: każdy policzony słup wariantu jest renderowany
 * TUTAJ do markupu HTML — tym samym dokumentem co strona karty
 * (TechCalcCardDocument) — bo frontend jest jedynym rendererem tych
 * dokumentów. Markup + zserializowany CSS aplikacji lecą na backend
 * (/variants/{id}/cards-pdf/ lub /card-pdf/ dla pojedynczej karty), gdzie
 * headlessowy Chromium drukuje każdą kartę do PDF (A4, jedna strona — reguły
 * @page karty) i odsyła ZIP albo sam PDF.
 */

/** Filename-safe slug: keeps letters/digits (incl. Polish), collapses the rest. */
function slug(s: string): string {
  return s.replace(/[^\p{L}\p{N}._-]+/gu, "_").replace(/^_+|_+$/g, "");
}

/**
 * Cały CSS aplikacji (Tailwind + index.css) zserializowany z żywego dokumentu —
 * karta to klasy Tailwinda, więc dokument drukowany na backendzie musi je
 * dostać w <style>. Arkusze cross-origin (Google Fonts) rzucają przy odczycie
 * cssRules i są pomijane — backendowy wrapper linkuje te same fonty osobno.
 */
function collectAppCss(): string {
  const chunks: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      chunks.push(
        Array.from(sheet.cssRules)
          .map((r) => r.cssText)
          .join("\n"),
      );
    } catch {
      // cross-origin stylesheet — not readable, skip
    }
  }
  return chunks.join("\n");
}

export interface CardFile {
  /** Filename inside the ZIP (already .pdf). */
  name: string;
  /** <title> of the printed document. */
  title: string;
  /** Card markup (TechCalcCardDocument), without the html/head wrapper. */
  markup: string;
}

/** One card rendered to {base-filename-slug, title, markup} — shared between
 *  the ZIP export and the single-card PDF (card-page print button). */
async function renderCard(params: {
  projectName: string;
  variantName: string;
  drawing: TechCalcDrawing;
  pole: TechCalcPointFeature;
  result: PoleResult | undefined;
  stale: boolean;
}): Promise<{ base: string; title: string; markup: string }> {
  // Dynamic import: the server renderer only enters the bundle when someone
  // actually exports cards (cached after the first call).
  const { renderToStaticMarkup } = await import("react-dom/server");
  const ref = params.pole.properties.ref;
  const label = poleDisplayLabel(params.pole.properties);
  const markup = renderToStaticMarkup(
    <TechCalcCardDocument
      projectName={params.projectName}
      variantName={params.variantName}
      drawing={params.drawing}
      pole={params.pole}
      result={params.result}
      stale={params.stale}
    />,
  );
  // Ref suffix keeps names unique when historical/full numbers repeat.
  const base = slug(label === ref ? ref : `${label}_${ref}`);
  return { base, title: `Karta obliczeń słupa ${label} — ${params.projectName}`, markup };
}

/**
 * Card markup for every computed pole of the variant (in results and still on
 * the drawing), ordered and index-prefixed so file managers list them in the
 * S1, S2, S10 order.
 */
export async function buildCardFiles(params: {
  projectName: string;
  variantName: string;
  drawing: TechCalcDrawing;
  results: VariantResults;
}): Promise<CardFile[]> {
  const { projectName, variantName, drawing, results } = params;
  const resultPoles = results.results?.poles ?? {};
  const poles = drawingPoints(drawing)
    .filter(
      (p) =>
        p.properties.kind === "pole" &&
        !p.properties.params.out_of_scope &&
        p.properties.ref in resultPoles,
    )
    .sort((a, b) => compareRefs(a.properties.ref, b.properties.ref));
  if (!poles.length) return [];

  const pad = String(poles.length).length;
  return Promise.all(
    poles.map(async (pole, i) => {
      const { base, title, markup } = await renderCard({
        projectName,
        variantName,
        drawing,
        pole,
        result: resultPoles[pole.properties.ref],
        stale: results.stale,
      });
      return {
        name: `${String(i + 1).padStart(Math.max(pad, 2), "0")}_karta_${base}.pdf`,
        title,
        markup,
      };
    }),
  );
}

/** Render the cards, have the backend print them to PDF and return the ZIP;
 *  throws a user-facing (Polish) error when there is nothing to pack. */
export async function downloadCardsPdfZip(params: {
  variantId: string;
  projectName: string;
  variantName: string;
  drawing: TechCalcDrawing;
  results: VariantResults;
}): Promise<{ blob: Blob; count: number }> {
  const files = await buildCardFiles(params);
  if (!files.length) {
    throw new Error(
      "Brak wygenerowanych kart — przelicz obliczenia tego wariantu i spróbuj ponownie.",
    );
  }
  const blob = await api.renderCardsPdfZip(params.variantId, {
    css: collectAppCss(),
    cards: files,
  });
  return { blob, count: files.length };
}

/** Render ONE card and have the backend print it to a single PDF — the card
 *  page's print button. Same document and Chromium pipeline as the ZIP export,
 *  so the file carries no browser print header/footer (title, URL, page no.). */
export async function downloadCardPdf(params: {
  variantId: string;
  projectName: string;
  variantName: string;
  drawing: TechCalcDrawing;
  pole: TechCalcPointFeature;
  result: PoleResult | undefined;
  stale: boolean;
}): Promise<{ blob: Blob; filename: string }> {
  const { base, title, markup } = await renderCard(params);
  const filename = `karta_${base}.pdf`;
  const blob = await api.renderCardPdf(params.variantId, {
    css: collectAppCss(),
    cards: [{ name: filename, title, markup }],
  });
  return { blob, filename };
}
